import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY, catchError, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';

/** Endpoints que não precisam de token Bearer */
const PUBLIC_ENDPOINTS = [
  '/api/auth/login',
  '/api/auth/register-org',
  '/api/auth/refresh',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/accept-invite',
  '/api/auth/change-temporary-password',
];

function isPublicEndpoint(url: string): boolean {
  return PUBLIC_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

function addBearerToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Pular endpoints públicos
  if (isPublicEndpoint(req.url)) {
    return next(req);
  }

  // Token expirando em breve → refresh proativo antes do request
  if (authService.isTokenExpiringSoon() && authService.getRefreshToken()) {
    return authService.refresh().pipe(
      switchMap(() => {
        const newToken = authService.getAccessToken();
        if (!newToken) return EMPTY;
        return next(addBearerToken(req, newToken));
      }),
      catchError(() => {
        // Usar clearStorage() em vez de logout() para evitar nova chamada HTTP
        // que o interceptor interceptaria, causando loop recursivo
        authService.clearStorage();
        router.navigate(['/login']);
        return EMPTY;
      }),
    );
  }

  // Adicionar Bearer token se disponível
  const token = authService.getAccessToken();
  if (!token) return next(req);

  return next(addBearerToken(req, token)).pipe(
    catchError((err) => {
      if (err.status === 401) {
        authService.clearStorage();
        router.navigate(['/login']);
      }
      return throwError(() => err);
    }),
  );
};
